import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, Clock, Trash2, FileText, Plus, Edit2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available modalities
const MODALITIES = [
  'Swimming', 'Bike', 'Run', 'T1', 'T2', 'SKIERG', 'SLED PUSH', 
  'Rowing', 'Obstacle Course', 'Other'
];

export default function CourseTimingUpload({ eventId, onTimingsChange }) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [courseTiming, setCourseTiming] = useState(null);
  const [editingTiming, setEditingTiming] = useState(null);
  
  // Manual entry form state
  const [manualData, setManualData] = useState({
    modality: 'Swimming',
    start_time: '',
    first_finisher_time: '',
    last_finisher_time: '',
    cutoff_time: ''
  });

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // Fetch course timing data
  useEffect(() => {
    fetchCourseTiming();
  }, [eventId]);

  const fetchCourseTiming = async () => {
    try {
      const response = await axios.get(`${API}/course-timing/event/${eventId}`, config);
      setCourseTiming(response.data);
    } catch (error) {
      console.error('Failed to fetch course timing:', error);
    }
  };


  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.csv', '.txt', '.pdf', '.docx', '.doc', '.xlsx', '.xls'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExt)) {
        toast.error('Unsupported file type. Please upload CSV, TXT, PDF, DOCX, or XLSX files.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('event_id', eventId);

    try {
      const response = await axios.post(`${API}/course-timing/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Course timing uploaded and parsed successfully');
      
      if (response.data.modality_timings && response.data.modality_timings.length > 0) {
        toast.info(`Found ${response.data.modality_timings.length} modality timings`);
      }
      
      setUploadDialogOpen(false);
      setSelectedFile(null);
      fetchCourseTiming();
      if (onTimingsChange) onTimingsChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload course timing');
    } finally {
      setUploading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualData.modality) {
      toast.error('Please select a modality');
      return;
    }

    const formData = new FormData();
    formData.append('event_id', eventId);
    formData.append('modality', manualData.modality);
    if (manualData.start_time) formData.append('start_time', manualData.start_time);
    if (manualData.first_finisher_time) formData.append('first_finisher_time', manualData.first_finisher_time);
    if (manualData.last_finisher_time) formData.append('last_finisher_time', manualData.last_finisher_time);
    if (manualData.cutoff_time) formData.append('cutoff_time', manualData.cutoff_time);

    try {
      await axios.post(`${API}/course-timing/manual`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Timing entry added successfully');
      setManualDialogOpen(false);
      setManualData({
        modality: 'Swimming',
        start_time: '',
        first_finisher_time: '',
        last_finisher_time: '',
        cutoff_time: ''
      });
      fetchCourseTiming();
      if (onTimingsChange) onTimingsChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add timing entry');
    }
  };

  const handleEditSubmit = async () => {
    if (!editingTiming) return;

    const formData = new FormData();
    if (editingTiming.modality) formData.append('modality', editingTiming.modality);
    if (editingTiming.start_time) formData.append('start_time', editingTiming.start_time);
    if (editingTiming.cutoff_time) formData.append('cutoff_time', editingTiming.cutoff_time);
    if (editingTiming.first_finisher_time) formData.append('first_finisher_time', editingTiming.first_finisher_time);
    if (editingTiming.last_finisher_time) formData.append('last_finisher_time', editingTiming.last_finisher_time);

    try {
      await axios.put(`${API}/course-timing/${eventId}/modality/${editingTiming.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Timing entry updated successfully');
      setEditDialogOpen(false);
      setEditingTiming(null);
      fetchCourseTiming();
      if (onTimingsChange) onTimingsChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timing entry');
    }
  };

  const handleDelete = async (timingId) => {
    if (!window.confirm('Are you sure you want to delete this timing entry?')) return;

    try {
      await axios.delete(`${API}/course-timing/${eventId}/modality/${timingId}`, config);
      toast.success('Timing entry deleted successfully');
      fetchCourseTiming();
      if (onTimingsChange) onTimingsChange();
    } catch (error) {
      toast.error('Failed to delete timing entry');
    }
  };

  const openEditDialog = (timing) => {
    setEditingTiming({ ...timing });
    setEditDialogOpen(true);
  };


  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Course Timing</h3>
        <div className="flex gap-2">
          <Button onClick={() => setManualDialogOpen(true)} size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Manual
          </Button>
          <Button onClick={() => setUploadDialogOpen(true)} size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
        </div>
      </div>

      {/* Timings Table */}
      <div className="border rounded-lg overflow-hidden bg-white/50 backdrop-blur-sm">
        {courseTiming && courseTiming.modality_timings && courseTiming.modality_timings.length > 0 ? (
          <table className="w-full">
            <thead className="bg-emerald-50 border-b">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-semibold">Modality</th>
                <th className="px-4 py-2 text-left text-sm font-semibold">Start Time</th>
                <th className="px-4 py-2 text-left text-sm font-semibold">Cutoff Time</th>
                <th className="px-4 py-2 text-left text-sm font-semibold">First Finisher</th>
                <th className="px-4 py-2 text-left text-sm font-semibold">Last Finisher</th>
                <th className="px-4 py-2 text-center text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {courseTiming.modality_timings.map((timing) => (
                <tr key={timing.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{timing.modality}</td>
                  <td className="px-4 py-2 text-sm">{timing.start_time || '-'}</td>
                  <td className="px-4 py-2 text-sm">{timing.cutoff_time || '-'}</td>
                  <td className="px-4 py-2 text-sm">{timing.first_finisher_time || '-'}</td>
                  <td className="px-4 py-2 text-sm">{timing.last_finisher_time || '-'}</td>
                  <td className="px-4 py-2">
                    <div className="flex justify-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(timing)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(timing.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12 text-gray-400">
            <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No timing data yet</p>
            <p className="text-sm mt-1">Add timing data manually or upload a file to get started</p>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Course Timing File</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Upload File (CSV, TXT, PDF, DOCX, XLSX)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept=".csv,.txt,.pdf,.docx,.doc,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-emerald-50 file:text-emerald-700
                    hover:file:bg-emerald-100
                    cursor-pointer"
                />
              </div>
              {selectedFile && (
                <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {selectedFile.name}
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-900 mb-1">How AI Parsing Works:</p>
              <ul className="text-blue-700 space-y-1 text-xs">
                <li>• Upload timing data in any supported format</li>
                <li>• AI extracts modality-specific timing (start, finish, cutoff, first athlete)</li>
                <li>• Timing data is used to suggest photographer arrival/departure times</li>
                <li>• You can edit or add more timing entries after upload</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
                {uploading ? 'Uploading...' : 'Upload & Parse'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Timing Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Modality *</Label>
              <select
                value={manualData.modality}
                onChange={(e) => setManualData({ ...manualData, modality: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {MODALITIES.map(mod => (
                  <option key={mod} value={mod}>{mod}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={manualData.start_time}
                  onChange={(e) => setManualData({ ...manualData, start_time: e.target.value })}
                />
              </div>

              <div>
                <Label>Cutoff Time</Label>
                <Input
                  type="time"
                  value={manualData.cutoff_time}
                  onChange={(e) => setManualData({ ...manualData, cutoff_time: e.target.value })}
                />
              </div>

              <div>
                <Label>First Finisher Time</Label>
                <Input
                  type="time"
                  value={manualData.first_finisher_time}
                  onChange={(e) => setManualData({ ...manualData, first_finisher_time: e.target.value })}
                />
              </div>

              <div>
                <Label>Last Finisher Time</Label>
                <Input
                  type="time"
                  value={manualData.last_finisher_time}
                  onChange={(e) => setManualData({ ...manualData, last_finisher_time: e.target.value })}
                />
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
              <p><strong>Tips:</strong></p>
              <ul className="mt-1 space-y-1">
                <li>• All time fields are optional - fill in what you know</li>
                <li>• This data helps calculate photographer arrival/departure times</li>
                <li>• You can edit or delete entries later</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setManualDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleManualSubmit}>
                Add Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      {editingTiming && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Timing Entry</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Modality *</Label>
                <select
                  value={editingTiming.modality}
                  onChange={(e) => setEditingTiming({ ...editingTiming, modality: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {MODALITIES.map(mod => (
                    <option key={mod} value={mod}>{mod}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={editingTiming.start_time || ''}
                    onChange={(e) => setEditingTiming({ ...editingTiming, start_time: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Cutoff Time</Label>
                  <Input
                    type="time"
                    value={editingTiming.cutoff_time || ''}
                    onChange={(e) => setEditingTiming({ ...editingTiming, cutoff_time: e.target.value })}
                  />
                </div>

                <div>
                  <Label>First Finisher Time</Label>
                  <Input
                    type="time"
                    value={editingTiming.first_finisher_time || ''}
                    onChange={(e) => setEditingTiming({ ...editingTiming, first_finisher_time: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Last Finisher Time</Label>
                  <Input
                    type="time"
                    value={editingTiming.last_finisher_time || ''}
                    onChange={(e) => setEditingTiming({ ...editingTiming, last_finisher_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditSubmit}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
